import React, { useMemo, useState } from 'react';
import { Badge, Button, Flex, Input, List, Pagination, Typography } from 'antd';
import { FilterOutlined, HistoryOutlined, SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  ChannelCategoryEntity,
  ChannelEntity,
  ChannelFilters,
  ChannelSortMode,
  channelSortMode,
  countActiveFilters,
  nextChannelSortMode,
  withChannelSortMode,
} from '../../../domain';
import { Pagination as PaginationVM } from '../../types/Pagination';
import { useIsMobile } from '../../../services/responsiveUtils';
import { debounce } from '../../../services/Debounce.service';
import EmptyState from '../EmptyState/EmptyState.component';
import ChannelCard from './ChannelCard.component';
import ChannelFiltersChips from './ChannelFiltersChips.component';
import ChannelFiltersSheet from './ChannelFiltersSheet.component';
import './ChannelPicker.styles.css';

export interface ChannelPickerProps {
  channels: ChannelEntity[];
  categories: ChannelCategoryEntity[];
  isLoading: boolean;
  pagination: PaginationVM;
  filters: ChannelFilters;
  selectedChannels: Record<string, boolean>;
  mode?: 'single' | 'multiple';
  showSelection?: boolean;
  onFiltersChange: (filters: ChannelFilters) => void;
  onSelectionChange: (selected: Record<string, boolean>) => void;
  onPageChange: (page: number, pageSize: number) => void;
  onLoadMore?: () => void;
  onSelectAll?: () => void;
  onPreviewCount?: (filters: ChannelFilters) => Promise<number>;
}

const SEARCH_DEBOUNCE_MS = 400;

const SORT_MODE_ICONS: Record<ChannelSortMode, React.ReactNode> = {
  usage: <HistoryOutlined />,
  'members-desc': <SortDescendingOutlined />,
  'members-asc': <SortAscendingOutlined />,
};

const ChannelPicker: React.FC<ChannelPickerProps> = ({
  channels,
  categories,
  isLoading,
  pagination,
  filters,
  selectedChannels,
  mode = 'multiple',
  showSelection = true,
  onFiltersChange,
  onSelectionChange,
  onPageChange,
  onLoadMore,
  onSelectAll,
  onPreviewCount,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isFiltersOpen, setFiltersOpen] = useState(false);

  const activeFiltersCount = countActiveFilters(filters);
  const selectedCount = Object.values(selectedChannels).filter(Boolean).length;
  const hasMore = channels.length < pagination.total;
  const sortMode = channelSortMode(filters);

  // Мемоизация обязательна: без неё на каждый рендер создаётся новый debounce и он не срабатывает.
  const handleSearchChange = useMemo(
    () => debounce((value: string) => onFiltersChange({ ...filters, search: value }), SEARCH_DEBOUNCE_MS),
    [filters, onFiltersChange],
  );

  const cycleSortMode = () => onFiltersChange(withChannelSortMode(filters, nextChannelSortMode(sortMode)));

  const handleToggle = (uuid: string, selected: boolean) => {
    if (mode === 'single') {
      onSelectionChange(selected ? { [uuid]: true } : {});
      return;
    }
    const next = { ...selectedChannels, [uuid]: selected };
    if (!selected) delete next[uuid];
    onSelectionChange(next);
  };

  const showEmptyState = channels.length === 0 && !isLoading;

  return (
    <Flex vertical gap="middle" className="channel-picker">
      <Input.Search
        placeholder={t('search-channels', { ns: 'labels' })}
        defaultValue={filters.search}
        onChange={(e) => handleSearchChange(e.target.value)}
        allowClear
      />

      <Flex gap="small" wrap>
        <Badge count={activeFiltersCount} size="small">
          <Button icon={<FilterOutlined />} onClick={() => setFiltersOpen(true)}>
            {t('channel-filters', { ns: 'labels' })}
          </Button>
        </Badge>
        <Button icon={SORT_MODE_ICONS[sortMode]} onClick={cycleSortMode}>
          {t(`channel-sort-${sortMode}`, { ns: 'labels' })}
        </Button>
      </Flex>

      <ChannelFiltersChips filters={filters} categories={categories} onChange={onFiltersChange} />

      {showEmptyState ? (
        <EmptyState
          title={activeFiltersCount > 0
            ? t('no-channels-for-filters', { ns: 'labels' })
            : t('no-channels-available', { ns: 'labels' })}
          description={activeFiltersCount > 0
            ? t('try-reset-filters', { ns: 'labels' })
            : t('no-channels-available-description', { ns: 'labels' })}
        />
      ) : (
        <List
          loading={isLoading}
          grid={{ gutter: 12, xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
          dataSource={channels}
          renderItem={(channel) => (
            <List.Item key={channel.uuid}>
              <ChannelCard
                channel={channel}
                mode={mode}
                showSelection={showSelection}
                isSelected={Boolean(selectedChannels[channel.uuid])}
                onToggle={handleToggle}
              />
            </List.Item>
          )}
        />
      )}

      {!showEmptyState && (isMobile ? (
        hasMore && onLoadMore ? (
          <Button block loading={isLoading} onClick={onLoadMore}>
            {t('load-more', { ns: 'buttons' })}
          </Button>
        ) : null
      ) : (
        <Flex justify="center">
          <Pagination
            current={pagination.page}
            total={pagination.total}
            pageSize={pagination.perPage}
            onChange={onPageChange}
            showSizeChanger={false}
          />
        </Flex>
      ))}

      {mode === 'multiple' && showSelection && (
        <div className="channel-picker__selection-bar">
          <Typography.Text>{t('selected-count', { ns: 'labels', count: selectedCount })}</Typography.Text>
          {onSelectAll && (
            <Button type="link" onClick={onSelectAll}>
              {t('select-all-channels', { ns: 'labels' })}
            </Button>
          )}
        </div>
      )}

      <ChannelFiltersSheet
        open={isFiltersOpen}
        isMobile={isMobile}
        filters={filters}
        categories={categories}
        totalCount={pagination.total}
        onClose={() => setFiltersOpen(false)}
        onApply={onFiltersChange}
        onPreviewCount={onPreviewCount}
      />
    </Flex>
  );
};

export default ChannelPicker;
